import React, { useCallback, useMemo } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { format, isFuture, isPast, millisecondsToMinutes } from 'date-fns';

import { EPGProgram } from 'custom_modules/react-native-epg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import isFunction from 'utils/function';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import { isWeb } from 'utils/responsiveness';

import { DURATION_LIMITATIONS } from 'screens/Epg/utils/parseRBProgramToEpgProgram';

import RecordingIcon from 'components/ContentMarkerIcons/RecordingMarker';
import { getRecordingStatusAccessibilityLabel } from 'components/ContentMarkerIcons/RecordingMarker/utils';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import { EpgProgramExtra, EpgProgramIcon } from '../../types';
import EpgLockedIcon from '../EpgLockedIcon';
import EpgReplayIcon from '../EpgReplayIcon';
import EpgUnsubscribedIcon from '../EpgUnsubscribedIcon';
import styles from './styles';

type Props = {
    program: EPGProgram<EpgProgramExtra>;
    playing: boolean;
    live: boolean;
    onAccessibilityTap?(program: EPGProgram<EpgProgramExtra>): void;
    stickyCellEnabled?: boolean;
};

const {
    EPG: { EPG_PROGRAM_TITLE_SHORT },
    CARDS_TITLE_CONTENT_LOCKED,
    ACCESSIBILITY: {
        EPG: { ACCESSIBILITY_EPG_START_END_TIME },
        DETAIL_PAGE: {
            ACCESSIBILITY_ICON_LOCKED,
            ACCESSIBILITY_ICON_CATCHUP,
            ACCESSIBILITY_ICON_SHOPPING_CART,
        },
    },
} = LANGUAGE_KEYS;

function EpgProgramCell({
    program,
    playing,
    live,
    onAccessibilityTap,
    stickyCellEnabled,
}: Props) {
    const {
        title,
        startTime,
        endTime,
        extra: {
            testID,
            iconTestID,
            time,
            subscribed,
            minimizedCell,
            isFirstCell,
            isLastCell,
            iconType,
            recordingItem,
            originalTitle,
        } = {},
    } = program;

    const { t } = useTranslation();
    const profileParentalControlRating = useProfileParentalRating();

    const durationInMin = useMemo(
        () => millisecondsToMinutes(endTime - startTime),
        [endTime, startTime],
    );

    const soIcon = useMemo(
        () =>
            (live || isFuture(startTime)) &&
            iconType === EpgProgramIcon.REPLAY_SO,
        [live, iconType, startTime],
    );
    const cuIcon = useMemo(
        () => !live && isPast(endTime) && iconType === EpgProgramIcon.REPLAY_CU,
        [live, endTime, iconType],
    );
    const isContentLocked = useMemo(
        () =>
            needsToLockContent(
                profileParentalControlRating,
                program.extra?.rating,
            ),
        [program, profileParentalControlRating],
    );

    const renderTitle = useCallback(() => {
        if (minimizedCell && title === EPG_PROGRAM_TITLE_SHORT) {
            return (
                <View style={styles.programTitleCollapsed}>
                    <View
                        style={[
                            styles.programTitleCollapsedDot,
                            styles.programTitleCollapsedDotIndent,
                            playing && styles.programTitleCollapsedDotPlaying,
                        ]}
                    />
                    <View
                        style={[
                            styles.programTitleCollapsedDot,
                            styles.programTitleCollapsedDotIndent,
                            playing && styles.programTitleCollapsedDotPlaying,
                        ]}
                    />
                    <View
                        style={[
                            styles.programTitleCollapsedDot,
                            playing && styles.programTitleCollapsedDotPlaying,
                        ]}
                    />
                </View>
            );
        }

        if (
            minimizedCell &&
            title !== EPG_PROGRAM_TITLE_SHORT &&
            isContentLocked
        ) {
            return null;
        }

        return (
            <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                category={ETextCategory.body2}
                color={playing ? ETextColor.black : ETextColor.neutral}
                style={styles.programCellText}
                bold={playing}
            >
                {isContentLocked ? t(CARDS_TITLE_CONTENT_LOCKED) : title}
            </Text>
        );
    }, [minimizedCell, title, isContentLocked, playing]);

    const renderIcon = useCallback(() => {
        if (recordingItem && durationInMin >= DURATION_LIMITATIONS.TRUNCATED) {
            return (
                <RecordingIcon
                    highlighted={playing}
                    recordingItem={recordingItem}
                    collectionSuspensionActive={false}
                    size={20}
                    style={styles.recordingIcon}
                />
            );
        }

        if (
            isContentLocked &&
            durationInMin >= DURATION_LIMITATIONS.TRUNCATED
        ) {
            return <EpgLockedIcon testID={iconTestID} playing={playing} />;
        }

        if (soIcon || cuIcon) {
            return <EpgReplayIcon testID={iconTestID} playing={playing} />;
        }

        if (iconType === EpgProgramIcon.NOT_SUBSCRIBED) {
            return (
                <EpgUnsubscribedIcon testID={iconTestID} playing={playing} />
            );
        }

        return null;
    }, [
        isContentLocked,
        soIcon,
        cuIcon,
        iconType,
        durationInMin,
        playing,
        recordingItem,
    ]);

    const accessibilityLabel = useMemo(() => {
        const programInfo = `${originalTitle}. ${format(
            startTime,
            'HH:mm',
        )} ${t(ACCESSIBILITY_EPG_START_END_TIME)} ${format(endTime, 'HH:mm')}`;

        if (recordingItem && durationInMin >= DURATION_LIMITATIONS.TRUNCATED) {
            return `${programInfo}. ${t(
                getRecordingStatusAccessibilityLabel({
                    recordingItem,
                    collectionSuspensionActive: false,
                }),
            )}`;
        }

        if (
            isContentLocked &&
            durationInMin >= DURATION_LIMITATIONS.TRUNCATED
        ) {
            return `${programInfo}. ${t(ACCESSIBILITY_ICON_LOCKED)}`;
        }

        if (soIcon || cuIcon) {
            return `${programInfo}. ${t(ACCESSIBILITY_ICON_CATCHUP)}`;
        }

        if (iconType === EpgProgramIcon.NOT_SUBSCRIBED) {
            return `${programInfo}. ${t(ACCESSIBILITY_ICON_SHOPPING_CART)}`;
        }

        return programInfo;
    }, [
        cuIcon,
        durationInMin,
        endTime,
        iconType,
        isContentLocked,
        originalTitle,
        recordingItem,
        soIcon,
        startTime,
        t,
    ]);

    const handleAccessibilityTap = useCallback(() => {
        if (isFunction(onAccessibilityTap)) {
            onAccessibilityTap(program);
        }
    }, [onAccessibilityTap, program]);

    return (
        <View
            testID={testID}
            accessible
            accessibilityLabel={accessibilityLabel}
            accessibilityRole="button"
            onAccessibilityTap={handleAccessibilityTap}
            style={[
                styles.programCell,
                isFirstCell && styles.programCellStartedOnThePreviousDay,
                isLastCell && styles.programCellEndingOnTheNextDay,
                live && styles.programCellLive,
                playing && styles.programCellPlaying,
                isWeb && stickyCellEnabled && styles.webStickyProgramCell,
                minimizedCell && styles.minimizedTextContainer,
            ]}
        >
            <View
                style={[
                    styles.programCellInner,
                    isWeb && stickyCellEnabled && styles.webStickyText,
                    minimizedCell && styles.minimizedText,
                    isWeb && !subscribed && styles.unsubscribedCell,
                ]}
            >
                <View
                    style={[
                        styles.programCellInnerInfo,
                        minimizedCell && styles.programCellInnerInfoCollapsed,
                    ]}
                >
                    <View style={gStyles.flexRow}>
                        {renderTitle()}
                        {renderIcon()}
                    </View>
                    {!minimizedCell && (
                        <Text
                            numberOfLines={1}
                            category={ETextCategory.caption2}
                            color={
                                playing
                                    ? ETextColor.black
                                    : ETextColor.light_grey
                            }
                            style={styles.programCellText}
                        >
                            {time}
                        </Text>
                    )}
                </View>
            </View>
            {!subscribed && <View style={styles.unsubscribedOverlay} />}
        </View>
    );
}

export default EpgProgramCell;
